import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "bg-[#1E222D] border-[#2A2E39] text-[#EAECEF] placeholder:text-[#5E6673] focus-visible:ring-[#F0B90B]/50";
const labelCls = "text-[11px] font-medium uppercase tracking-wider text-[#848E9C]";

export default function EditClientDialog({ client, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({ name: client.name, email: client.email, phone: client.phone || "", password: "" });
    }
  }, [client]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setLoading(true);
    try {
      const payload = { name: form.name, email: form.email, phone: form.phone };
      if (form.password) payload.password = form.password;
      await api.patch(`/admin/clients/${client.id}`, payload);
      toast.success(`Client ${form.name} updated`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-client-modal" className="border-[#2A2E39] bg-[#131722] text-[#EAECEF] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">
            Edit Client <span className="font-mono text-sm text-[#F0B90B]">#{client?.account_no}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-[#848E9C]">
            Update client details. Setting a new password logs the client out of all sessions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className={labelCls}>Full Name</Label>
            <Input data-testid="edit-client-name-input" value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Email</Label>
            <Input data-testid="edit-client-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Phone Number</Label>
            <Input data-testid="edit-client-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>New Password (leave empty to keep current)</Label>
            <Input data-testid="edit-client-password-input" type="text" value={form.password} onChange={set("password")} placeholder="Min. 6 characters" className={inputCls} />
          </div>
          <button
            data-testid="submit-edit-client-btn"
            onClick={submit}
            disabled={loading}
            className="w-full rounded-md bg-[#F0B90B] py-2.5 text-sm font-semibold text-black shadow-[0_0_12px_rgba(240,185,11,0.25)] transition-colors hover:bg-[#F8D12F] disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
